import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Typography,
  InputAdornment,
  Box,
  Paper,
} from "@material-ui/core";
import {
  DataGrid,
  getGridNumericColumnOperators,
} from "@material-ui/data-grid/";
import { WatchActions } from "../../../../state";
import {
  moneyFormatter,
  GridButtons,
  getSiteSpecificPrice,
  getSiteSpecificRetailPrice,
  getSiteSpecificStatus,
  getSiteSpecificDateAdded,
  format_Word,
  statusColor,
} from "../../../../lib";
import { EditWatchDetails } from ".";
import { makeStyles } from "@material-ui/core/styles";
import { QuickSearchToolbar, escapeRegExp } from "../../../../lib/utils";

const useStyles = makeStyles((theme) => ({
  dataGrid: {
    '& .MuiDataGrid-row': {
      '&:hover': {
        backgroundColor: 'rgba(63, 81, 181, 0.04)',
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
      fontSize: '0.8rem',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#fafafa',
      borderBottom: '2px solid rgba(224, 224, 224, 1)',
      fontSize: '0.8rem',
      fontWeight: 600,
    },
    '& .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
  card: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: theme.spacing(3),
    borderRadius: 8,
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'transparent',
  },
}));
export function WatchDetails(props) {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [groupDetailsData, setGroupDetailsData] = useState();
  const [myBrandId, setMyBrandId] = useState();
  const [isDisabled, setIsDisabled] = useState(true);
  const siteId = useSelector((state) => state.scopes.site_id);

  const {
    pageRoute: {
      params: { attribute, id, detail, brand },
    },
  } = props;
  useEffect(() => {
    (async function () {
      // Always load ALL connected watches regardless of status. The old
      // "All Watches" toggle + its active-only default were removed (the
      // default relied on a stale statuses.ACTIVE constant that filtered to
      // the wrong status).
      const data = await WatchActions.getDetails(attribute, id, siteId, true);
      setRows(data);
    })();
  }, [attribute, id, siteId]);

  const price = {
    type: "parseFloat",
    flex: 0.4,
    valueFormatter: ({ value }) => moneyFormatter(Number(value), siteId),
    headerAlign: "left",
    align: "left",
    extendType: "parseFloat",
    filterOperators: getGridNumericColumnOperators()
      .filter((operator) => operator.value === ">" || operator.value === "<")
      .map((operator) => {
        return {
          ...operator,
          InputComponentProps: {
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            },
          },
        };
      }),
  };

  const detailsColumns = [
    { field: "model", headerName: "Model", flex: 1 },
    { field: "title", headerName: "Title", flex: 2, sort: "asc" },
    { field: "price", headerName: "Price", ...price },
    { field: "retail", headerName: "Retail Price", ...price },
    {
      field: "status",
      headerName: "Status",
      flex: 0.4,
      renderCell: (params) => {
        return statusColor(params.value);
      },
      filterable: false,
    },
    { field: "dateAdded", headerName: "Date Added", type: "date", flex: 0.5 },
    {
      field: "buttons",
      headerName: "View",
      flex: 0.7,
      renderCell: (params) => {
        return GridButtons(params.value);
      },
      filterable: false,
    },
  ];

  const detailsRows = rows?.map((row) => {
    return {
      id: row.item_id,
      model: row.model_no,
      title: row.title,
      price: getSiteSpecificPrice(row.price, siteId),
      retail: getSiteSpecificRetailPrice(row, siteId),
      status: getSiteSpecificStatus(row, siteId),
      dateAdded: getSiteSpecificDateAdded(row, siteId),
      buttons: row,
    };
  });
  const [searchText1, setSearchText1] = useState("");
  const [rows1, setRows1] = useState(detailsRows);
  const requestSearch = (searchValue) => {
    console.log(searchValue);
    setSearchText1(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), "i");
    const filteredRows = detailsRows.filter((row) => {
      return Object.keys(row).some((field) => {
        return searchRegex.test(row[field]?.toString());
      });
    });
    setRows1(filteredRows);
  };
  useEffect(() => {
    if (detailsRows?.length) {
      setRows1(detailsRows);
    }
  }, [rows]);
  return (
    <Box style={{ width: "100%" }}>
      <Box display="flex" justifyContent="center" mb={2}>
        <Typography variant="h6">
          {format_Word(attribute) + " — " + detail + " (ID: " + id + ")"}
        </Typography>
      </Box>
      <Paper className={classes.card}>
        <EditWatchDetails
          brandTitle={brand}
          attribute={attribute}
          id={id}
          detail={detail}
          rawAttribute={attribute}
          path={
            attribute === "warranty" ||
            attribute === "vendor" ||
            attribute === "delivery"
              ? "manage-siteId-attributes"
              : attribute === "series"
              ? "watch-attribute-affiliated"
              : "watch-attributes"
          }
          setGroupDetailsData={setGroupDetailsData}
          setMyBrandId={setMyBrandId}
          myBrandId={myBrandId}
          setIsDisabled={setIsDisabled}
          isDisabled={isDisabled}
        />
        <DataGrid
          className={classes.dataGrid}
          rows={rows1}
          columns={detailsColumns}
          disableColumnMenu={true}
          isRowSelectable={false}
          autoHeight={true}
          pageSize={100}
          checkboxSelection={false}
          rowHeight={38}
          components={{ Toolbar: QuickSearchToolbar }}
          componentsProps={{
            toolbar: {
              value: searchText1,
              onChange: (event) => {
                requestSearch(event.target.value);
              },
              clearSearch: () => requestSearch(""),
            },
          }}
          sortModel={[{ field: "title", sort: "asc" }]}
        />
      </Paper>
    </Box>
  );
}
